/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Button,
  Group,
  Modal,
  TextInput,
  Alert,
  Stack,
  Text,
  Loader,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import ModalHeader from "components/ModalHeader";
import { Location, useAddPunchOut } from "api/attendance/punchOut";
import { getCurrentLocation } from "utils/devicegeolocation";
import { useGetOrg } from "api/organizations/getOrg";
import { useUser } from "hooks/useUser";

type PunchOutModalProps = {
  opened: boolean;
  orgId?: any;
  onClose: () => void;
};

interface EarlyDepartureConfig {
  graceMinutes?: number;
  markHalfDayBeforeMinutes?: number;
  notesRequired?: boolean;
}

interface WorkingHoursConfig {
  endTime: string;
  totalHours: number;
}

export default function PunchOutModal({
  opened,
  orgId,
  onClose,
}: PunchOutModalProps) {
  const { mutateAsync: addPunchOut, isLoading, isError } = useAddPunchOut();
  const [isNotesRequired, setIsNotesRequired] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);

  // Get org data (we'll need to extract org ID from somewhere - using from localStorage or user context)

  const { data: orgData } = useGetOrg(orgId);
  console.log(orgData?.workingHours, "ordata");

  // Form with conditional validation
  const form = useForm({
    initialValues: { notes: "" },
    validate: {
      notes: (value) => {
        if (isNotesRequired) {
          return value.trim().length > 0
            ? null
            : "Notes are required when clocking out more than 10 minutes before or after end time.";
        }
        return null;
      },
    },
  });

  // Detect if notes are required when modal opens
  useEffect(() => {
    if (!opened) return;

    const detectNotesRequirement = async () => {
      try {
        setIsLoadingConfig(true);

        const workingHoursArray = (orgData as any)?.workingHours || [];
        let endTimeString = "16:00"; // Default fallback
        const currentTime = new Date();
        
        // Match today's date formatted as YYYY-MM-DD
        const year = currentTime.getFullYear();
        const month = String(currentTime.getMonth() + 1).padStart(2, "0");
        const day = String(currentTime.getDate()).padStart(2, "0");
        const todayString = `${year}-${month}-${day}`;

        if (Array.isArray(workingHoursArray) && workingHoursArray.length > 0) {
          // First check for today's date match
          const todayConfig = workingHoursArray.find(
            (wh: any) => wh.date === todayString,
          );
          
          if (todayConfig && todayConfig.endTime) {
            endTimeString = todayConfig.endTime;
          } else {
            // Find the default config (type: "default") if today's date doesn't match
            const defaultConfig = workingHoursArray.find(
              (wh: any) => wh.type === "default",
            );
            if (defaultConfig && defaultConfig.endTime) {
              endTimeString = defaultConfig.endTime;
            }
          }
        }

        const [endHour, endMinute] = endTimeString.split(":").map(Number);
        const expectedEndTime = new Date(currentTime);
        expectedEndTime.setHours(endHour, endMinute, 0, 0);

        const diffMs = currentTime.getTime() - expectedEndTime.getTime();
        const diffMinutes = diffMs / (1000 * 60);

        // Notes required if user clock out before 10 min of end time (-10 min) or after 10 min of end time (+10 min)
        if (diffMinutes < -10 || diffMinutes > 10) {
          setIsNotesRequired(true);
        } else {
          setIsNotesRequired(false);
        }
      } catch (error) {
        console.error("Error detecting notes requirement:", error);
        setIsNotesRequired(false);
      } finally {
        setIsLoadingConfig(false);
      }
    };

    detectNotesRequirement();
  }, [opened, orgData]);

  const handleSubmit = async () => {
    const validation = form.validate();
    if (validation.hasErrors) {
      return;
    }

    try {
      const timestamp = new Date().toISOString();
      let location: Location = { lat: 0, lng: 0 };
      try {
        const { lat, lng } = await getCurrentLocation();
        location = { lat, lng };
      } catch (geoError) {
        console.log(geoError);
      }
      const payload = {
        punchOutTime: timestamp,
        notes: form.values.notes,
        location,
      };

      await addPunchOut(payload);

      form.reset();
      setIsNotesRequired(false);
      onClose();
    } catch (err) {
      console.error("Clock Out Failed:", err);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      overlayOpacity={0.5}
      shadow="xs"
      withCloseButton={false}
      transitionDuration={300}
      centered
    >
      <ModalHeader
        title="Clock Out"
        onClose={onClose}
        ariaLabel="Close Clock Out modal"
      />

      {isLoadingConfig ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack spacing="sm">
            <div>
              <TextInput
                withAsterisk={isNotesRequired}
                label="Notes"
                placeholder="Enter your notes"
                {...form.getInputProps("notes")}
              />

              {isNotesRequired && (
                <Text size="xs" color="dimmed" mt={4}>
                  Notes are mandatory when clocking out more than 10 minutes before or after end time.
                </Text>
              )}
            </div>

            {isError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
              >
                Failed to save. Please try again.
              </Alert>
            )}

            <Group position="right" spacing={20} mt={10}>
              <Button color="cancel" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="filled"
                onClick={handleSubmit}
                loading={isLoading}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
